import {
  errorResponseSchema,
  IDEMPOTENCY_KEY_HEADER,
  successResponseSchema,
  type ErrorResponse,
} from '@fuel-system/contracts';
import { z } from 'zod';
import { ApiRequestError, ApiTimeoutError, UNKNOWN_REQUEST_ID } from './api-error.js';
import { currentLocale } from './i18n.js';
import { captureError } from './monitoring.js';

export { ApiRequestError, ApiTimeoutError, UNKNOWN_REQUEST_ID } from './api-error.js';

const BASE_URL = import.meta.env['VITE_API_URL'] ?? '/api/v1';

/**
 * Rule E8: `fetch` has no timeout of its own, and rule D8 keeps a drawer open
 * and every exit blocked for as long as its submit stays pending - together,
 * with no deadline, a hung mutation traps the user with no way out. This
 * bounds every request so it always eventually settles, one way or another.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 20_000;

interface RequestOptions<TResponse> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Response schema. Parsing here means a backend change fails loudly. */
  schema?: z.ZodType<TResponse>;
  signal?: AbortSignal;
  /** Rule E8. Overridable for tests and long-running exceptions; defaults to DEFAULT_REQUEST_TIMEOUT_MS. */
  timeoutMs?: number;
  /**
   * Extra headers, merged over the defaults below (e.g. the idempotency-key
   * header on a write — web-team-rules.md "Every API call goes through the
   * app's apiRequest ... Writes send the idempotency-key header").
   */
  headers?: Record<string, string>;
}

async function parseError(response: Response): Promise<ErrorResponse> {
  try {
    const json: unknown = await response.json();
    const parsed = errorResponseSchema.safeParse(json);
    if (parsed.success) return parsed.data;
  } catch {
    // Falls through to the synthetic envelope below.
  }

  return {
    success: false,
    statusCode: response.status,
    error: {
      code: response.status >= 500 ? 'INTERNAL_ERROR' : 'UNPROCESSABLE',
      message: response.statusText || 'Request failed',
    },
    requestId: response.headers.get('x-request-id') ?? UNKNOWN_REQUEST_ID,
    timestamp: new Date().toISOString(),
    locale: currentLocale(),
    path: response.url,
  };
}

export async function apiRequest<TResponse = unknown>(
  path: string,
  options: RequestOptions<TResponse> = {},
): Promise<TResponse> {
  const {
    method = 'GET',
    body,
    schema,
    signal,
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
    headers: extraHeaders,
  } = options;

  // Rule E8: the deadline covers the whole exchange, including the single
  // transparent 401-refresh retry below, so a stuck retry cannot hide behind a
  // fresh clock. `fetch` only gives up when its signal aborts, so a deadline
  // controller is the only way to bound it; `timedOut` is what tells a
  // deliberate caller cancellation (rule E17, case 2 - a plain abort) apart
  // from this deadline firing (an unknown outcome, never a plain failure).
  const deadline = new AbortController();
  let timedOut = false;
  const onExternalAbort = (): void => deadline.abort();
  if (signal) {
    if (signal.aborted) deadline.abort();
    else signal.addEventListener('abort', onExternalAbort);
  }
  const timer =
    timeoutMs > 0
      ? setTimeout(() => {
          timedOut = true;
          deadline.abort();
        }, timeoutMs)
      : undefined;

  try {
    const send = async (): Promise<Response> =>
      fetch(`${BASE_URL}${path}`, {
        method,
        // Cookie session (docs/rebuild/unified-access.md section 3): this
        // app's own session cookie, set by POST /auth/:app/launch. There is
        // no bearer token to attach.
        credentials: 'include',
        headers: {
          'Accept-Language': currentLocale(),
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...extraHeaders,
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        signal: deadline.signal,
      });

    let response: Response;
    try {
      response = await send();
    } catch (error) {
      throw timedOut ? new ApiTimeoutError() : error;
    }

    if (!response.ok) {
      const payload = await parseError(response);
      const failure = new ApiRequestError(response.status, payload);
      // captureError itself ignores 4xx; a rejected form is not an incident.
      captureError(failure, { path });
      throw failure;
    }

    if (response.status === 204) return undefined as TResponse;

    const json: unknown = await response.json();
    const envelope = successResponseSchema(z.unknown()).parse(json);
    // The API lifts a paginated result into the envelope (data = the rows,
    // meta = the pagination; response-envelope.interceptor.ts). Put it back
    // together so a `paginated(row)` schema sees { data, meta }.
    const payload =
      envelope.meta !== null && Array.isArray(envelope.data)
        ? { data: envelope.data, meta: envelope.meta }
        : envelope.data;
    return (schema ? schema.parse(payload) : payload) as TResponse;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onExternalAbort);
  }
}

/**
 * A fresh idempotency key per logical submit (web-team-rules.md): the caller
 * generates one when a drawer/dialog opens or a submit begins, and reuses the
 * same value across retries of that same submit.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function idempotencyHeader(key: string): Record<string, string> {
  return { [IDEMPOTENCY_KEY_HEADER]: key };
}

/**
 * Extracts a filename from a `Content-Disposition` header, falling back to a
 * caller-supplied default when the header is missing or unparsable.
 */
function filenameFrom(response: Response, fallback: string): string {
  const header = response.headers.get('Content-Disposition');
  if (!header) return fallback;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const plainMatch = /filename="?([^";]+)"?/i.exec(header);
  return plainMatch?.[1] ?? fallback;
}

/**
 * Downloads a file (Excel/PDF export, year-management backup) and saves it
 * through an object URL + anchor, exactly like a normal browser download.
 * Goes through the same cookie session and error envelope as `apiRequest`;
 * only the success path differs (a blob instead of a parsed JSON payload).
 */
export async function downloadFile(
  path: string,
  options: {
    query?: Record<string, string | undefined>;
    method?: 'GET' | 'POST';
    body?: unknown;
    filename?: string;
    signal?: AbortSignal;
  } = {},
): Promise<void> {
  const { query, method = 'GET', body, filename, signal } = options;
  const search: [string, string][] = query
    ? (Object.entries(query).filter(([, value]) => value !== undefined) as [string, string][])
    : [];
  const queryString = search.length > 0 ? `?${new URLSearchParams(search).toString()}` : '';

  const response = await fetch(`${BASE_URL}${path}${queryString}`, {
    method,
    credentials: 'include',
    headers: {
      'Accept-Language': currentLocale(),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    signal,
  });

  if (!response.ok) {
    const payload = await parseError(response);
    const failure = new ApiRequestError(response.status, payload);
    captureError(failure, { path });
    throw failure;
  }

  const blob = await response.blob();
  const resolvedFilename = filenameFrom(response, filename ?? 'download');
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = resolvedFilename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
